import React, { ReactNode } from 'react';
import { Box, Column, Columns, MarkdownLite, Stack, Text } from '@origin-digital/ods-core';
import { IconPromoTag } from '@origin-digital/ods-icons';
import { IEFixedAlert } from '../utils/IEFixedAlert';
import {
  DiscountPerkAccordionType,
  PlanCreditPerk,
  PlanCreditPerkPromoBox,
  RatePercentage,
  StaticPerkAccordionType,
} from '../../typings';

export const OriginGoVariablePromoBox = () => {
  const heading = '50% off usage rates';
  const content =
    'Currently, the usage rates for this plan are 50% less than the usage rates for our ' +
    'Origin Basic plan. Note that rates for both plans are variable and may change from time to time.';
  return (
    <IEFixedAlert
      muiProps={{
        style: {
          maxWidth: '100%',
        },
      }}
      icon={false}
      tone={'promote'}
      className="muiAlert-message"
      data-id={'usage-rates-promo-box'}
    >
      <Stack space="xsmall">
        <Columns space="medium">
          <Column>
            <Stack space="xxsmall">
              <Text weight="medium" tone="neutralDark">
                {heading}
              </Text>
              <Text data-id="usage-rates-content-main">
                <MarkdownLite>{content}</MarkdownLite>
              </Text>
            </Stack>
          </Column>
          <Column width="content" alignX="right">
            {<IconPromoTag size="medium" color={'promote'} />}
          </Column>
        </Columns>
      </Stack>
    </IEFixedAlert>
  );
};

const UsageRatePerkBody: ReactNode = (
  <Text data-id="usage-rates-content-main">
    Currently, the usage rates for this plan are 50% less than the usage rates for our Origin Basic
    plan. Note that rates for both plans are variable and may change from time to time.
  </Text>
);

const UsageRatePerkSummary: ReactNode = <Text weight="medium">50% off usage rates</Text>;
const UsageRatePerkIcon =
  'https://res.cloudinary.com/originenergy/image/upload/v1736983338/icons/ic-LocalOffer_green.svg';
const UsageRateId = 'usage-rate-accordion';
const RateId = 'rate-accordion';
const PlanCreditId = 'plan-credit-accordion';

export const UsageRatePerkContent: StaticPerkAccordionType = {
  accordionIcon: UsageRatePerkIcon,
  accordionSummary: UsageRatePerkSummary,
  accordionBody: UsageRatePerkBody,
  accordionDataId: UsageRateId,
  expanded: true,
};

const RatePerkBodyGas = ({ percentage }: RatePercentage) => {
  return (
    <Text data-id="usage-rates-content-main">
      Currently, the rates for this natural gas plan are {percentage}% less than the rates for our
      Origin Basic plan. Note that rates for both plans are variable and may change from time to
      time.
    </Text>
  );
};

const RatePerkSummaryGas = ({ percentage }: RatePercentage) => {
  return <Text weight="medium">{percentage}% off natural gas rates</Text>;
};

export const getRatePerkContentGas = (): DiscountPerkAccordionType => {
  return {
    accordionIcon: UsageRatePerkIcon,
    accordionSummary: ({ percentage }) => <RatePerkSummaryGas percentage={percentage} />,
    accordionBody: ({ percentage }) => <RatePerkBodyGas percentage={percentage} />,
    accordionDataId: RateId,
    expanded: false,
  };
};

const PlanPerkIcon =
  'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/icons/ic-LocalOffer_promote.svg';

const PlanCreditPerkSummary = () => {
  return <Text weight="medium">Get credits on your bill</Text>;
};

const PlanCreditPerkBody = ({ isQLD }: PlanCreditPerk) => {
  const offerText = isQLD
    ? 'Join or move house with this plan to get **$150 bill credit (incl GST) for electricity**.'
    : 'Join or move house with this plan to get **$100 bill credit (incl GST) for both electricity and natural gas**.';

  const termsText = isQLD
    ? 'Sign up by 30/11/25 and stay until 31/12/25. QLD only. Only available in select postcodes in the Ergon network for customers without solar, and if you haven’t received a credit for the same fuel in the past 12 months at this address. Credit amounts include GST.*'
    : 'Sign up by 30/11/25 and stay until 31/12/25. VIC, SA, ACT, NSW only. Only available if you haven’t received a credit for the same fuel in the past 12 months at this address.';

  return (
    <Box data-id="plan-credit-perk-content">
      <Stack space="large">
        <Text>
          <MarkdownLite>{offerText}</MarkdownLite>
        </Text>
        <Text>{termsText}</Text>
      </Stack>
    </Box>
  );
};

export const getPlanCreditPerkPromoBox = (isQLD = false): PlanCreditPerkPromoBox => {
  return {
    accordionIcon: PlanPerkIcon,
    accordionSummary: <PlanCreditPerkSummary />,
    accordionBody: <PlanCreditPerkBody isQLD={isQLD} />,
    accordionDataId: PlanCreditId,
    expanded: false,
  };
};
